import { CameraOutlined } from '@ant-design/icons';
import { Alert, Button, Card, List, Space, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { CAMERA_PERMISSION_STATES } from '@/shared/camera/camera-permissions';
import { useValueScanner } from '@/shared/camera/useValueScanner';
import {
  VALUE_SCAN_CANDIDATE_KINDS,
  type ValueScanCandidate,
  type ValueScanPurpose,
} from '@/shared/camera/value-scanner';
import { messages } from './messages';

interface ValueScannerProps {
  autoAcceptHighConfidence?: boolean;
  children?: ReactNode;
  compact?: boolean;
  onAccept: (candidate: ValueScanCandidate) => Promise<void> | void;
  purpose: ValueScanPurpose;
  showCandidates?: boolean;
  title?: string;
}

export function ValueScanner({
  autoAcceptHighConfidence = false,
  children,
  compact = false,
  onAccept,
  purpose,
  showCandidates = true,
  title,
}: ValueScannerProps) {
  const intl = useIntl();
  const scanner = useValueScanner({
    autoAcceptHighConfidence,
    autoStart: true,
    onAccept,
    purpose,
  });

  return (
    <Card size="small" title={title ?? intl.formatMessage(messages.scannerValueTitle)}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{intl.formatMessage(messages.scannerValueDescription)}</Typography.Text>

        {scanner.permissionState === CAMERA_PERMISSION_STATES.denied ||
        scanner.permissionState === CAMERA_PERMISSION_STATES.unsupported ? (
          <Alert message={intl.formatMessage(messages.scannerValueCameraNotGranted)} showIcon type="warning" />
        ) : null}

        {scanner.isActive || scanner.permissionState !== CAMERA_PERMISSION_STATES.denied ? (
          <>
            <video
              muted
              playsInline
              ref={scanner.videoRef}
              style={{
                background: '#000',
                borderRadius: 8,
                display: 'block',
                height: compact ? 'clamp(150px, 22vh, 180px)' : undefined,
                maxHeight: compact ? 180 : 360,
                objectFit: 'cover',
                width: '100%',
              }}
            />
            <canvas hidden ref={scanner.canvasRef} />
          </>
        ) : (
          <canvas hidden ref={scanner.canvasRef} />
        )}

        {scanner.error ? <Alert message={scanner.error} showIcon type="warning" /> : null}

        {showCandidates && scanner.candidates.length > 0 ? (
          <List
            dataSource={scanner.candidates}
            renderItem={candidate => (
              <List.Item
                actions={[
                  <Button key="use" onClick={() => void onAccept(candidate)} type="link">
                    {intl.formatMessage(messages.scannerActionsUse)}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  description={
                    <Space size="small" wrap>
                      <Tag
                        color={candidate.kind === VALUE_SCAN_CANDIDATE_KINDS.barcode ? 'blue' : 'green'}
                        icon={<CameraOutlined />}
                      >
                        {candidate.kind === VALUE_SCAN_CANDIDATE_KINDS.barcode
                          ? intl.formatMessage(messages.scannerCandidateQrCode)
                          : intl.formatMessage(messages.scannerCandidateText)}
                      </Tag>
                      <Typography.Text type="secondary">{Math.round(candidate.confidence * 100)}%</Typography.Text>
                    </Space>
                  }
                  title={candidate.value}
                />
              </List.Item>
            )}
            size="small"
          />
        ) : null}

        {children}
      </Space>
    </Card>
  );
}
